import React, { useState, useEffect, useContext, useCallback } from "react";
import moment from "moment";
import { useQuery, useLazyQuery } from "@apollo/client";
import {
  ActionList,
  Button,
  DatePicker,
  Link,
  Modal,
  PageActions,
  Popover,
  RadioButton,
  ResourceList,
  InlineStack,
  BlockStack,
  Tabs,
  Text,
} from "@shopify/polaris";
import { Banner } from "lib/components";
import { baseHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";
import { GET_EXPORT_FILTER, CHECK_ORDER_LINES } from "app/orders/apollo/queries";
import { useNavigate } from "react-router-dom";
import operatorExportOrderProps from "./props";

const OperatorExportOrder = (props) => {
  const { isOpenModal, setIsOpenModal } = props;
  const { cms, currentUser = {} } = useContext(PrivateContext);
  const { createdAt } = currentUser;
  const navigate = useNavigate();
  const { loading: getExportFilterLoading, data: getExportFilter } = useQuery(GET_EXPORT_FILTER);
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
    onDismiss: null,
  });

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([[], []]);
  const [vendorList, setVendorList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [fulfillmentStatus, setFulfillmentStatus] = useState([]);
  const [orderStatus, setOrderStatus] = useState([]);
  const [exportAll, setExportAll] = useState(true);
  const [isPrimaryActionLoading, setPrimaryAction] = useState(false);
  const [exportLink, setExportLink] = useState(false);
  const [exportPDFLink, setExportPDFLink] = useState(false);
  const [generate, setGenerate] = useState(false);
  const [isModalClickable, setIsModalClickable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState("");
  const [queryData, setQueryData] = useState({});
  const [submitDate, setSubmitDate] = useState({});
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectDate, setSelectDate] = useState();
  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const exportOrderUrl = "/export/order";
  const [
    getCheckOrderList,
    { loading: checkOrderLineLoading, data: checkOrderLineData },
  ] = useLazyQuery(CHECK_ORDER_LINES, { variables: { input: { ...queryData } } });

  const disableDate = (newDate) => {
    newDate.setDate(newDate.getDate() - 1);
    return newDate;
  };

  const handleMonthChange = useCallback((monthValue, yearValue) => setDate({ month: monthValue, year: yearValue }), []);

  useEffect(() => {
    if (isOpenModal) {
      setIsCalendarOpen(false);
    }
  }, [isOpenModal]);

  useEffect(() => {
    let bannerData = false;
    if (!checkOrderLineLoading && checkOrderLineData && generate) {
      const responseData = baseHelper.getResponseData(checkOrderLineData, constant.gql.CHECK_ORDER_LINES);
      if (!responseData) {
        bannerData = {
          title:
            baseHelper.getResponseError(checkOrderLineData, constant.gql.CHECK_ORDER_LINES) ||
            cms("message.error.noOrder"),
          status: constant.CRITICAL,
        };
      }

      if (bannerData) {
        setPrimaryAction(false);
        setBanner({ ...bannerData, isOpen: true });
      } else {
        const csvDownloadRoute = `${exportOrderUrl}${params || ""}`;
        const pdfDownloadRoute = `${exportOrderUrl}/pdf${params || ""}`;

        setPrimaryAction(false);
        setExportLink(csvDownloadRoute);
        setExportPDFLink(pdfDownloadRoute);
      }
      setLoading(false);
    }
  }, [checkOrderLineLoading, checkOrderLineData, exportAll, params, cms]);

  useEffect(() => {
    if (getExportFilterLoading || !getExportFilter) {
      return;
    }
    const exportFilterData = baseHelper.getResponseData(getExportFilter, constant.gql.GET_EXPORT_FILTER);
    if (!exportFilterData) {
      setBanner({
        status: constant.CRITICAL,
        title: baseHelper.getResponseError(getExportFilter, constant.gql.GET_EXPORT_FILTER),
        isOpen: true,
      });
      return;
    }
    const { vendorFilter = [], statusFilter = [], fulfillmentStatuses = [], orderStatuses = [] } = exportFilterData;
    if (!(statusFilter && statusFilter.length)) {
      setIsModalClickable(false);
      return;
    }
    setStatusList(statusFilter);
    setFulfillmentStatus(fulfillmentStatuses);
    setOrderStatus(orderStatuses);
    setVendorList(vendorFilter);
  }, [getExportFilter, getExportFilterLoading]);

  const exportOrder = {
    label: {
      resourceName: [
        { singular: cms("section.exportOrder.label.vendor"), plural: cms("section.exportOrder.label.vendors") },
        { singular: cms("section.exportOrder.label.status"), plural: cms("section.exportOrder.label.statuses") },
      ],
    },
    exportModalTabs: [
      {
        id: "vendors",
        content: cms("section.exportOrder.label.vendor"),
        panelID: "panel-vendors",
      },
      {
        id: "status",
        content: cms("section.exportOrder.label.status"),
        panelID: "panel-status",
      },
    ],
  };

  const onExport = (isSpecificExport = false) => {
    setLoading(true);
    setGenerate(true);
    setPrimaryAction(true);
    let bannerData = false;
    let status = [];
    let vendor = [];
    let fulfillmentStatusNew = [];
    let orderStatusNew = [];

    vendor = [...selectedOptions[0]];
    status = [...selectedOptions[1]];
    fulfillmentStatusNew = status.filter((item) => item && fulfillmentStatus.includes(item));
    orderStatusNew = status.filter((item) => item && orderStatus.includes(item));
    const fulfillmentString = fulfillmentStatusNew.join(",");
    const orderString = orderStatusNew.join(",");
    const vendorString = vendor.join(",");
    const hasAppliedDateRange = Boolean(submitDate.isShowDate && submitDate.start && submitDate.end);
    if (!(exportAll || vendorString || fulfillmentString || orderString)) {
      setLoading(false);
      bannerData = {
        title: cms("message.error.pleaseSelect"),
        status: constant.CRITICAL,
      };
      setPrimaryAction(false);
      setBanner({ ...bannerData, isOpen: true });
      return;
    }
    if (isSpecificExport) {
      const updatedSelectedOptions = baseHelper.cloneData(selectedOptions);
      if (!selectedTabIndex) {
        setParams(`?vendor=${vendorString}`);
        updatedSelectedOptions[1] = [];
        orderStatusNew = [];
        fulfillmentStatusNew = [];
      } else {
        setParams(`?status=${orderString}&fulfillmentStatus=${fulfillmentString}`);
        updatedSelectedOptions[0] = [];
        vendor = [];
      }
      setSelectedOptions(updatedSelectedOptions);
    }
    let url = exportAll ? "" : `?vendor=${vendorString}&status=${orderString}&fulfillmentStatus=${fulfillmentString}`;
    if (hasAppliedDateRange) {
      const dateQuery = `startDate=${submitDate.start}&endDate=${submitDate.end}`;
      url = url ? `${url}&${dateQuery}` : `?${dateQuery}`;
    }
    setParams(url);
    const dataToFetch = {};
    if (orderStatusNew.length > 0) {
      dataToFetch.status = orderStatusNew;
    }
    if (fulfillmentStatusNew.length > 0) {
      dataToFetch.fulfillmentStatus = fulfillmentStatusNew;
    }
    if (vendor.length > 0) {
      dataToFetch.vendor = vendor;
    }
    if (hasAppliedDateRange) {
      dataToFetch.dateRange = {
        start: moment(submitDate.start).format("ll"),
        end: moment(submitDate.end).format("ll"),
      };
    }
    setQueryData(dataToFetch);
    getCheckOrderList();
    setExportLink(false);
    setExportPDFLink(false);
  };

  const toggleBanner = () => {
    setBanner({
      isOpen: false,
      status: "",
      title: "",
    });
  };

  const getResourceListTabs = () => {
    const exportModalTabs = baseHelper.cloneData(exportOrder.exportModalTabs);
    const singularItem = cms("section.exportOrder.label.item");
    const pluralItem = cms("section.exportOrder.label.items");
    const changeIndex = 0;
    selectedOptions.forEach((item, index) => {
      if (item.length > 0) {
        exportModalTabs[index + changeIndex].content += ` (${item.length} ${
          item.length === 1 ? singularItem : pluralItem
        } ${cms("section.exportOrder.label.Selected")})`;
      }
    });
    return exportModalTabs;
  };
  const closeModal = () => {
    setSelectedTabIndex(0);
    setSelectedOptions([[], []]);
    setExportAll(true);
    setPrimaryAction(false);
    setExportLink(false);
    setExportPDFLink(false);
    setIsOpenModal(false);
    setLoading(false);
    toggleBanner();
    setGenerate(false);
    setSubmitDate({});
    setSelectDate();
    setIsCalendarOpen(false);
  };

  const downloadCSV = () => {
    window.open(exportLink);
    closeModal();
  };

  const downloadPDF = () => {
    window.open(exportPDFLink);
    closeModal();
  };

  const handleSelectionChange = (selectedItems) => {
    const prevSelectedOptions = [...selectedOptions];
    prevSelectedOptions[selectedTabIndex] = [...selectedItems];
    setSelectedOptions(prevSelectedOptions);
    setExportLink(false);
    setExportPDFLink(false);
    setLoading(false);
  };

  const handleTabChange = (selectedTabInde) => {
    setSelectedTabIndex(selectedTabInde);
    setExportLink(false);
    setExportPDFLink(false);
  };

  const items = [vendorList, statusList];
  const updateResourceName = [...exportOrder.label.resourceName];

  const promotedBulkActions = [
    {
      content: cms("section.exportOrder.label.generate"),
      onAction: () => onExport(true),
    },
  ];
  const [active, setActive] = useState(false);
  const [popoverActive, setPopoverActive] = useState(false);

  const handleChange = () => {
    setActive(!active);
    setPopoverActive(!popoverActive);
  };

  const activator = (
    <Button onClick={handleChange} disclosure>
      {cms("section.exportOrder.label.selectFormat")}
    </Button>
  );
  const togglePopoverActive = useCallback(() => setPopoverActive(!popoverActive), [popoverActive]);

  const handleDatePicker = () => {
    setSelectDate({ start: selectDate.start, end: selectDate.end });
    setSubmitDate((prev) => {
      const initialState = { ...prev };
      initialState.start = selectDate.start;
      initialState.end = selectDate.end;
      initialState.startingDate = moment(selectDate.start).format("ll");
      initialState.endingDate = moment(selectDate.end).format("ll");
      initialState.isShowDate = true;
      return initialState;
    });
    setIsCalendarOpen(false);
    setExportLink(false);
    setExportPDFLink(false);
    setGenerate(false);
  };

  const handleCancelDate = () => {
    const initialState = { ...submitDate };
    setSelectDate({
      start: initialState.start || new Date(),
      end: initialState.end || new Date(),
    });
    setIsCalendarOpen(false);
    setExportLink(false);
    setExportPDFLink(false);
  };

  const handlePopover = () => {
    return (
      <Popover active={popoverActive} activator={activator} onClose={togglePopoverActive}>
        <ActionList
          items={[
            {
              content: cms("section.exportOrder.label.exportCSV"),
              onAction: () => {
                setLoading(false);
                downloadCSV();
              },
            },
            {
              content: cms("section.exportOrder.label.exportPDF"),
              onAction: () => {
                setLoading(false);
                downloadPDF();
              },
            },
          ]}
        />
      </Popover>
    );
  };
  

  return (
    <>
      <Modal
        open={isOpenModal}
        onClose={() => closeModal()}
        title={cms("section.exportOrder.label.exportOrder")}
      >
        <Modal.Section>
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
          />
          {isCalendarOpen && (
            <>
              <div>
                <DatePicker
                  month={month}
                  year={year}
                  onChange={(date) => {
                    setSelectDate({ start: date.start, end: date.end });
                  }}
                  onMonthChange={handleMonthChange}
                  selected={selectDate}
                  disableDatesAfter={new Date()}
                  disableDatesBefore={createdAt ? disableDate(new Date(createdAt)) : undefined}
                  multiMonth
                  allowRange
                />
              </div>
              <div>
                <PageActions
                  primaryAction={{
                    content: "Apply",
                    id: "modalCustomButton",
                    onClick: () => {
                      handleDatePicker();
                    },
                  }}
                  secondaryActions={[
                    {
                      content: cms("common.button.cancel"),
                      onClick: () => {
                        handleCancelDate();
                      },
                    },
                  ]}
                />
              </div>
              <br />
            </>
          )}
          {!isCalendarOpen && (
            <BlockStack>
              <InlineStack align="space-between" blockAlign="start">
                <RadioButton
                  id="exportAllRadio"
                  name={constant.EXPORT}
                  label={cms("section.exportOrder.label.exportAll")}
                  helpText={cms("section.exportOrder.label.exportAllText")}
                  checked={exportAll}
                  onChange={() => {
                    setGenerate(false);
                    setBanner({ isOpen: false });
                    setExportAll(true);
                    setExportPDFLink(false);
                    setExportLink(false);
                  }}
                />
                <BlockStack>
                  <Button
                    variant="plain"
                    onClick={() => {
                      setIsCalendarOpen(true);
                    }}
                  >
                    Select Date Range
                    {/* {cms("sectionContent.export.label.date")} */}
                  </Button>
                  {submitDate.isShowDate && (
                    <Text as="span" tone="subdued">
                      <Text variant="bodySm" as="p">{`${submitDate.startingDate} - ${submitDate.endingDate}`}</Text>
                    </Text>
                  )}
                </BlockStack>
              </InlineStack>
              <RadioButton
                id="exportSelectedRadio"
                name={constant.EXPORT}
                label={cms("section.exportOrder.label.exportSelected")}
                helpText={cms("section.exportOrder.label.exportSelectedText")}
                checked={!exportAll}
                onChange={() => {
                  setGenerate(false);
                  setExportAll(false);
                  setExportPDFLink(false);
                  setBanner({ isOpen: false });
                  setExportLink(false);
                }}
              />
            </BlockStack>
          )}
          {!exportAll && !isCalendarOpen && (
            <>
              <Tabs
                key="tabs"
                tabs={getResourceListTabs()}
                selected={selectedTabIndex}
                onSelect={handleTabChange}
                fitted
              >
                <ResourceList
                  key="resourceList"
                  resourceName={updateResourceName[selectedTabIndex]}
                  items={items[selectedTabIndex]}
                  renderItem={(item) => {
                    const { id, name, location } = item;
                    const replaceName = {
                      noShow: constant.NOSHOW,
                    };
                    const statusName = name.replace(/\b(?:noShow)\b/gi, (matched) => replaceName[matched]);
                    return (
                      <ResourceList.Item id={id} accessibilityLabel={`${cms("section.export.label.view")} ${id}`}>
                        <h3>
                          <Text as="span" fontWeight="semibold">
                            {baseHelper.ucFirst(statusName)}
                          </Text>
                        </h3>
                        <div>{location}</div>
                      </ResourceList.Item>
                    );
                  }}
                  selectedItems={selectedOptions[selectedTabIndex]}
                  onSelectionChange={handleSelectionChange}
                  selectable
                  promotedBulkActions={promotedBulkActions}
                  loading={getExportFilterLoading}
                />
              </Tabs>
            </>
          )}
        </Modal.Section>
        <Modal.Section>
          <InlineStack gap="100" align="space-between">
            <Link onClick={() => navigate("/setting/order/export")}>{cms("section.exportOrder.label.customize")}</Link>
            <InlineStack gap="400">
              <Button onClick={() => closeModal()}>{cms("section.exportOrder.label.cancel")}</Button>
              {exportLink ? (
                handlePopover()
              ) : (
                <Button variant="primary" id="modalCustomButton" onClick={() => onExport()} loading={loading}>
                  {cms("section.exportOrder.label.generate")}
                </Button>
              )}
            </InlineStack>
          </InlineStack>
        </Modal.Section>
      </Modal>
    </>
  );
};

OperatorExportOrder.propTypes = operatorExportOrderProps.type;

export default OperatorExportOrder;
